<template>
    <div class="import-feedback-drawer-mapping-step">
        <pendo-card class="import-feedback-drawer-mapping-step__container">
            <h3>Mapping</h3>
            <p>
                Map the columns to ensure that the data from the source file is accurately transferred to Pendo Listen.
            </p>
            <pendo-table
                :data="mappingTableData"
                :columns="mappingTableColumns"
                title="Map columns"
                max-height="1000">
                <template #headerLeft>
                    Map columns
                </template>
                <template #pendoField="{ row }">
                    <div class="import-feedback-drawer-mapping-step__pendo-field">
                        <p>
                            {{ row.pendoField.label }}
                            <span>{{ row.required ? '*' : '' }}</span>
                        </p>
                        <pendo-icon
                            type="arrow-right"
                            size="16" />
                    </div>
                </template>
                <template #pendoFieldHeader="{ column }">
                    <div class="import-feedback-drawer-mapping-step__pendo-field-header">
                        <img
                            class="filled-icon"
                            width="24"
                            height="24"
                            src="@img/icons/pendo-logo-icon.svg">
                        <span>
                            {{ column.label }}
                        </span>
                    </div>
                </template>
                <template #sourceField="{ row }">
                    <pendo-multiselect
                        :options="allOptions(row)"
                        :value="selectedColumn(row)"
                        full-width
                        hide-selected-options
                        placeholder="(Select a column)"
                        @select="onMapColumn(row, $event)">
                        <template #suffix>
                            <pendo-icon
                                v-if="selectedColumn(row)"
                                class="clear-search"
                                type="x"
                                size="14"
                                tag="button"
                                @click="clearSelectedColumn(row, $event)" />
                        </template>
                        <template #noData>
                            All columns are mapped.
                        </template>
                        <template #option="{ option }">
                            <div
                                v-if="doesOptionMatchRegex(option)"
                                class="import-feedback-drawer-mapping-step__mapping-option">
                                {{ option.label
                                }}<span v-if="previewValue(option)">
                                    - (<span class="import-feedback-drawer-mapping-step__mapping-option--example">{{
                                        previewValue(option)
                                    }}</span>)</span>
                            </div>
                            <div
                                v-else
                                class="import-feedback-drawer-mapping-step__mapping-option">
                                {{ option.label }}
                            </div>
                        </template>
                    </pendo-multiselect>
                </template>
                <template #example="{ row }">
                    {{ selectedColumnExample(row) }}
                </template>
                <template #mappingStatus="{ row }">
                    <div
                        v-if="isColumnMapped(row)"
                        class="import-feedback-drawer-mapping-step__mapping-status">
                        <pendo-icon
                            type="check"
                            size="14"
                            color="var(--palette-green-90)" />
                        <span>Confirmed mapping</span>
                    </div>
                    <div
                        v-else
                        class="import-feedback-drawer-mapping-step__mapping-status">
                        <pendo-icon
                            type="info"
                            size="14"
                            color="var(--palette-orange-70)" />
                        <span class="import-feedback-drawer-mapping-step__mapping-status--not-mapped">Not mapped</span>
                    </div>
                </template>
            </pendo-table>
            <div class="import-feedback-drawer-mapping-step__mapped-column-number">
                {{ numberOfMappedColumnsText }}
            </div>
        </pendo-card>
    </div>
</template>

<script>
import { PendoTable, PendoCard, PendoIcon, PendoMultiselect } from '@pendo/components';
import isNull from 'lodash/isNull';

const noColumnString = '-- Not provided in file --';

export default {
    components: {
        PendoTable,
        PendoCard,
        PendoIcon,
        PendoMultiselect,
    },
    props: {
        headers: {
            type: Array,
            required: true,
        },
        previewData: {
            type: Array,
            required: true,
        },
        mappingTableData: {
            type: Array,
            required: true,
        },
        mappedColumns: {
            type: Object,
            required: true,
        },
    },
    emits: ['map-column'],
    data () {
        return {
            mappingTableColumns: [
                {
                    label: 'Feedback item field',
                    prop: 'pendoField',
                    minWidth: 200,
                },
                {
                    label: 'Source column in your file',
                    prop: 'sourceField',
                    minWidth: 220,
                },
                {
                    label: 'Example value',
                    prop: 'example',
                    minWidth: 200,
                },
                {
                    label: 'Mapping status',
                    prop: 'mappingStatus',
                    minWidth: 190,
                },
            ],
        };
    },
    computed: {
        allOptions () {
            return (row) => {
                return [
                    ...this.headers.filter((_header, index) => {
                        return !Object.values(this.mappedColumns).includes(index);
                    }),
                    ...(row.required || this.mappedColumns[row.pendoField.value] === null
                        ? []
                        : [
                            {
                                id: -1,
                                label: noColumnString,
                            },
                        ]),
                ];
            };
        },
        selectedColumn () {
            return (row) => {
                const mappedIndex = this.mappedColumns[row.pendoField.value];

                if (isNull(mappedIndex)) {
                    return {
                        id: -1,
                        label: noColumnString,
                    };
                }

                return this.headers[mappedIndex];
            };
        },
        selectedColumnExample () {
            return (row) => {
                return this.previewData[0][this.headers[this.mappedColumns[row.pendoField.value]]?.label] || '--';
            };
        },
        doesOptionMatchRegex () {
            return (option) => {
                const validColumnLabelRegex = new RegExp(/^Column\s\d*$/);

                return validColumnLabelRegex.test(option.label);
            };
        },
        previewValue () {
            return (option) => {
                return this.previewData[0][option.label];
            };
        },
        isColumnMapped () {
            return (row) => {
                return !(this.mappedColumns[row.pendoField.value] === undefined);
            };
        },
        numberOfMappedColumnsText () {
            const numberOfMappedColumns = Object.keys(this.mappedColumns).length;
            const totalNumberOfColumnsToMap = this.mappingTableData.length;

            return `${numberOfMappedColumns}/${totalNumberOfColumnsToMap} columns mapped`;
        },
    },
    methods: {
        onMapColumn (row, column) {
            const index = this.headers.findIndex((header) => header.label === column.label);
            this.$emit('map-column', row.pendoField.value, index === -1 ? null : index);
        },
        clearSelectedColumn (row, event) {
            event.stopPropagation();
            this.$emit('map-column', row.pendoField.value, undefined);
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer-mapping-step {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__container {
        width: 70%;
        padding: 12px 20px 20px 20px;

        h3 {
            margin: 0;
            font-size: 22px;
            font-weight: 600;
        }

        p {
            margin: 8px 0 22px 0;
            font-size: 14px;
            line-height: 1.3;
        }
    }

    &__pendo-field {
        display: flex;
        align-items: center;

        p {
            margin: 0;
            font-size: 14px;
            font-weight: 600;

            span {
                margin-left: -2px;
                font-weight: 400;
                color: var(--palette-red-60);
            }
        }

        .pendo-icon {
            margin-left: auto;
        }
    }

    &__pendo-field-header {
        display: flex;
        align-items: center;

        .filled-icon {
            margin-right: 8px;
        }
    }

    .clear-search {
        background: unset;
        border: unset;
        padding: 0 10px;
    }

    &__mapping-option {
        line-height: 26px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &--example {
            font-style: italic;
        }
    }

    &__mapping-status {
        display: flex;
        align-items: center;

        .pendo-icon {
            margin-right: 8px;
        }

        &--not-mapped {
            color: var(--palette-gray-70);
        }
    }

    &__mapped-column-number {
        margin: 28px 0 8px 0;
        float: right;
    }
}
</style>
